import { Button, Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Course, useCourse } from 'api/courses';

import { Helmet } from 'react-helmet';

import { Box, CustomBox, GridChild, GridContainer } from 'common';
import { useEffect, useState } from 'react';
import { CourseCard } from 'widgets/cards/courseCard';
import { useLanguage } from '../../context/LanguageContext';
import { coursesPageLabels } from '../../locales/translations'

export const CoursesPage = () => {
    const { language } = useLanguage();
    const [courseList, setCourseList] = useState<Course[]>([]);
    const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const title = coursesPageLabels.title[language];
    const pageTitle = coursesPageLabels.pageTitle[language];
    const metaDescription = coursesPageLabels.metaDescription[language];

    const allCategory = coursesPageLabels.allCategory[language];
    const [selectedCategory, setSelectedCategory] = useState<string>(allCategory);

    const { onGetAllCourse } = useCourse();

    useEffect(() => {
        setSelectedCategory(allCategory);
    }, [allCategory]);

    useEffect(() => {
        (async () => {
            try {
                const result = await onGetAllCourse();
                if (result.success) {
                    const courses = result.data
                        .filter((course: Course) => course.show)
                        .sort((a: Course, b: Course) => 
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                        );
                    setCourseList(courses);

                    const uniqueCategories: string[] = Array.from(new Set(courses.map((course: Course) => course[`category_${language}`])));
                    setCategories([allCategory, ...uniqueCategories]);
                    setFilteredCourses(courses);
                } else {
                    setError('Произошла ошибка при загрузке курсов');
                }
            } catch (error: any) {
                setError(error.message || 'Неизвестная ошибка');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, [onGetAllCourse, language]);

    useEffect(() => {
        if (selectedCategory === allCategory) {
            setFilteredCourses(courseList);
        } else {
            setFilteredCourses(courseList.filter((course) => course[`category_${language}`] === selectedCategory));
        }
        // eslint-disable-next-line
    }, [selectedCategory, courseList]);

    const handleCategoryClick = (category: string) => {
        setSelectedCategory(category);
    };
    return (
        <CustomBox title={title}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
            </Helmet>
            <Box sx={{ display: 'flex', flexDirection: 'row', pb: 2 }}>
                {categories.map((category) => (
                    <Button
                        key={category}
                        variant={selectedCategory === category ? 'contained' : 'outlined'}
                        onClick={() => handleCategoryClick(category)}
                        sx={{
                            margin: '0 8px',
                            backgroundColor: selectedCategory === category ? '#af0000' : 'transparent',
                            color: selectedCategory === category ? '#fff' : '#af0000',
                            '&:hover': {
                                backgroundColor: selectedCategory === category ? '#af0000' : 'rgba(175, 0, 0, 0.08)',
                            },
                            borderColor: '#af0000',
                            fontWeight: 700
                        }}
                    >
                        {category}
                    </Button>
                ))}
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <GridContainer>
                    {filteredCourses.map((coursesItem) => (
                        <GridChild md={3} key={coursesItem.id}>
                            <CourseCard coursesItem={coursesItem} />
                        </GridChild>
                    ))}
                </GridContainer>
            )}
        </CustomBox>
    );
};
